import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import SlideshowRoundedIcon from "@mui/icons-material/SlideshowRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChatRoundedIcon from "@mui/icons-material/ChatRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  STATIC_COPILOT_COSTS,
  deleteLectureMaterialCall,
  getCopilotCreditsCall,
  listLectureMaterialsCall,
  uploadCostForKind,
  uploadLectureMaterialCall,
} from "../../redux/api/copilot.api";
import CreditsBar from "./components/CreditsBar";
import InsufficientCreditsModal from "./components/InsufficientCreditsModal";
import UploadTile from "./components/UploadTile";
import AskCopilotChatDialog from "./components/AskCopilotChatDialog";

/*
  ─── Microlab Copilot — Hub ──────────────────────────────────
  Web dashboard equivalent of the mobile MicrolabCopilotHubScreen.
  Surfaces every entry point in one place:

    • Hero card with greeting + credits bar
    • 4 upload tiles (PDF / Images / PPT / Audio) that open the
      browser file picker, validate size, then POST to
      /lecture-lab/upload and navigate to the processing screen
      where status polling completes the flow.
    • Recent materials list — click a row to open its Study Pack
      (or to re-enter the processing screen for in-flight ones).
    • "Ask Copilot" CTA that opens the inline chat dialog
      (AskCopilotChatDialog) — backed by POST /ai for one-shot
      histology/embryology questions with optional image.

  Backend is shared with mobile: every credit deduction the user
  sees here lands on the same `lecture_credit_ledger` table the
  app already writes to. The mobile + web clients can be open
  side-by-side and the credit balance stays consistent.
*/

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB — matches mobile uploadLimits.ts
const SOURCE_TYPES = [
  {
    key: "pdf",
    label: "PDF",
    sublabel: "Slides, lecture notes",
    tint: "#3B82F6",
    icon: <PictureAsPdfRoundedIcon fontSize="inherit" />,
    accept: ".pdf,application/pdf",
  },
  {
    key: "image",
    label: "Images",
    sublabel: "Whiteboard, slides",
    tint: "#10B981",
    icon: <ImageRoundedIcon fontSize="inherit" />,
    accept: "image/*",
  },
  {
    key: "ppt",
    label: "PowerPoint",
    sublabel: ".ppt / .pptx",
    tint: "#F59E0B",
    icon: <SlideshowRoundedIcon fontSize="inherit" />,
    accept:
      ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation",
  },
  {
    key: "audio",
    label: "Audio",
    sublabel: "Recorded lecture",
    tint: "#A855F7",
    icon: <GraphicEqRoundedIcon fontSize="inherit" />,
    accept: "audio/*",
  },
];

function StatusPill({ status }) {
  const map = {
    ready: { label: "Summarized", color: "success", emphasis: 700 },
    processing: { label: "Processing", color: "info", emphasis: 700 },
    queued: { label: "Queued", color: "default", emphasis: 600 },
    failed: { label: "Failed", color: "error", emphasis: 700 },
  };
  const cfg = map[status] || { label: status || "Unknown", color: "default", emphasis: 600 };
  return (
    <Chip
      size="small"
      label={cfg.label}
      color={cfg.color}
      variant={cfg.color === "default" ? "outlined" : "filled"}
      sx={{ fontWeight: cfg.emphasis, fontSize: 11, letterSpacing: 0.3 }}
    />
  );
}

export default function MicrolabCopilotHub() {
  const navigate = useNavigate();
  const userName = useSelector((s) => s.auth?.user?.name || s.auth?.user?.firstName || "");

  const [credits, setCredits] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null); // 'pdf' | 'image' | 'ppt' | 'audio' | null
  const [uploadProgress, setUploadProgress] = useState(0);
  const [insufficientCreditsState, setInsufficientCreditsState] = useState(null);
  const [askDialogOpen, setAskDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // One hidden <input type="file"> per source type so the click
  // handler doesn't need to dynamically build/destroy DOM nodes.
  // We keep refs by key for direct .click() invocation.
  const fileInputsRef = useRef({});

  const refreshCredits = useCallback(async () => {
    const next = await getCopilotCreditsCall();
    if (next) setCredits(next);
  }, []);

  const refreshMaterials = useCallback(async () => {
    setLoadingMaterials(true);
    try {
      const list = await listLectureMaterialsCall();
      setMaterials(Array.isArray(list) ? list : []);
    } finally {
      setLoadingMaterials(false);
    }
  }, []);

  useEffect(() => {
    refreshCredits();
    refreshMaterials();
  }, [refreshCredits, refreshMaterials]);

  const costs = credits?.costs || STATIC_COPILOT_COSTS;
  const costForKind = useCallback(
    (kind, fileSizeBytes) => uploadCostForKind(kind, fileSizeBytes, costs),
    [costs],
  );

  const openPickerForKind = (kind) => {
    if (uploadingKind) return;
    const input = fileInputsRef.current[kind];
    if (!input) return;
    // Resetting `value` lets the user pick the SAME file twice
    // in a row without the change handler going silent.
    input.value = "";
    input.click();
  };

  const onFileChosen = async (kind, evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_UPLOAD_BYTES) {
      // We render the size in the rejection message so the user
      // can see exactly why their 80 MB file was refused.
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      window.alert(
        `That file is ${mb} MB — Microlab Copilot accepts up to 50 MB per upload. Try compressing the file or splitting it.`,
      );
      return;
    }

    const upfrontCost = costForKind(kind, file.size);
    if (credits && Number(credits.creditsRemaining) < upfrontCost) {
      setInsufficientCreditsState({
        required: upfrontCost,
        remaining: credits.creditsRemaining,
      });
      return;
    }

    setUploadingKind(kind);
    setUploadProgress(0);
    try {
      const response = await uploadLectureMaterialCall({
        file,
        sourceType: kind,
        onProgress: setUploadProgress,
      });

      if (response?.status === 402 || response?.code === "INSUFFICIENT_CREDITS") {
        setInsufficientCreditsState({
          required: response?.credits?.required ?? upfrontCost,
          remaining: response?.credits?.creditsRemaining ?? 0,
        });
        return;
      }

      if (response?.material?.id) {
        // Credits returned in the same response are already
        // the post-debit balance — refresh state right away
        // so the bar updates without a second request.
        if (response.credits) setCredits(response.credits);
        navigate(`/microlab-copilot/processing/${response.material.id}`);
        return;
      }

      // Anything else (422, 500, etc.) → surface a generic toast.
      window.alert(
        response?.message ||
          "Upload failed. Please retry — if it keeps failing, the file may not contain enough readable content.",
      );
    } finally {
      setUploadingKind(null);
      setUploadProgress(0);
      // Always clear the input so the same file can be retried.
      if (fileInputsRef.current[kind]) {
        fileInputsRef.current[kind].value = "";
      }
    }
  };

  const onClickMaterial = (m) => {
    if (m.status === "ready") {
      navigate(`/microlab-copilot/study/${m.id}`);
    } else if (m.status === "processing" || m.status === "queued") {
      navigate(`/microlab-copilot/processing/${m.id}`);
    } else if (m.status === "failed") {
      // Failed → let the user delete (no useful destination)
      setConfirmDelete(m);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete?.id) return;
    const ok = await deleteLectureMaterialCall(confirmDelete.id);
    setConfirmDelete(null);
    if (ok) {
      // Optimistic remove + background refresh to confirm
      setMaterials((prev) => prev.filter((x) => x.id !== confirmDelete.id));
      refreshMaterials();
    }
  };

  const heroGradient = useMemo(
    () =>
      "linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(168,85,247,0.08) 40%, rgba(20,184,166,0.06) 100%)",
    [],
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={4} sx={{ maxWidth: 1400, mx: "auto", px: { xs: 0.5, md: 1 } }}>
        {/* ─── Hero card ─────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            position: "relative",
            borderRadius: 4,
            p: { xs: 2.5, md: 3 },
            mb: 2.5,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.18),
            backgroundImage: heroGradient,
            overflow: "hidden",
          })}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems={{ xs: "stretch", md: "center" }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Avatar
                sx={(theme) => ({
                  width: 52,
                  height: 52,
                  backgroundImage: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                  boxShadow: `0 12px 28px ${alpha(theme.palette.info.main, 0.32)}`,
                })}
              >
                <AutoAwesomeRoundedIcon />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ letterSpacing: "-0.02em", lineHeight: 1.15 }}
                >
                  Microlab Copilot
                </MDTypography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                  {userName ? `Hi ${userName} — ` : ""}upload any lecture material and turn it into a personalized
                  study pack with summaries, quizzes, flashcards and a chat tutor. Or ask the Copilot a one-shot
                  histology / embryology question right from this page.
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="flex-end">
              <MDButton
                variant="gradient"
                color="info"
                startIcon={<ChatRoundedIcon />}
                onClick={() => setAskDialogOpen(true)}
              >
                Ask Copilot
              </MDButton>
            </Stack>
          </Stack>
          <Box sx={{ mt: 2.5 }}>
            <CreditsBar credits={credits} />
          </Box>
        </Paper>

        {/* ─── Upload tiles ──────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            borderRadius: 4,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            border: "1px solid",
            borderColor: theme.palette.divider,
          })}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            mb={1.5}
            spacing={0.8}
          >
            <Box>
              <MDTypography variant="h5" fontWeight="bold">
                Turn a lecture into a study pack
              </MDTypography>
              <Typography variant="caption" color="text.secondary">
                Up to 50 MB per file. Credits are deducted once extraction succeeds.
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              // Fit columns to available content width (AI panel can shrink the
              // main column without changing the viewport breakpoint).
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 210px), 1fr))",
              gap: 1.5,
              width: "100%",
            }}
          >
            {SOURCE_TYPES.map((t) => {
              const cost = costForKind(t.key, 0); // base cost (small PDF / single image / etc.)
              const isUploading = uploadingKind === t.key;
              return (
                <Box key={t.key} sx={{ position: "relative", minWidth: 0, width: "100%" }}>
                  <UploadTile
                    icon={t.icon}
                    label={t.label}
                    sublabel={t.sublabel}
                    costLabel={`${cost}+ credits`}
                    tint={t.tint}
                    disabled={!!uploadingKind}
                    onClick={() => openPickerForKind(t.key)}
                  />
                  {isUploading ? (
                    <Box
                      sx={(theme) => ({
                        position: "absolute",
                        inset: 0,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.background.paper, 0.85),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      })}
                    >
                      <CircularProgress
                        size={32}
                        variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
                        value={uploadProgress * 100}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Uploading… {Math.round(uploadProgress * 100)}%
                      </Typography>
                    </Box>
                  ) : null}
                  <Box
                    component="input"
                    type="file"
                    accept={t.accept}
                    ref={(el) => {
                      fileInputsRef.current[t.key] = el;
                    }}
                    sx={{ display: "none" }}
                    onChange={(e) => onFileChosen(t.key, e)}
                  />
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ─── Recent materials ──────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            borderRadius: 4,
            p: { xs: 2, md: 2.5 },
            border: "1px solid",
            borderColor: theme.palette.divider,
          })}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            spacing={1}
          >
            <Box>
              <MDTypography variant="h5" fontWeight="bold">
                Your study packs
              </MDTypography>
              <Typography variant="caption" color="text.secondary">
                Open a pack to see its summary, quizzes, flashcards and chat tutor.
              </Typography>
            </Box>
            <Tooltip arrow title="Refresh">
              <IconButton onClick={refreshMaterials} disabled={loadingMaterials} size="small">
                {loadingMaterials ? <CircularProgress size={16} /> : <RefreshRoundedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>

          {loadingMaterials && materials.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : materials.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                py: 5,
                textAlign: "center",
                borderRadius: 2,
                borderStyle: "dashed",
              }}
            >
              <RestartAltRoundedIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No study packs yet. Upload your first lecture above to get started.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1.1}>
              {materials.map((m) => (
                <Paper
                  key={m.id}
                  variant="outlined"
                  onClick={() => onClickMaterial(m)}
                  sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.12)}`,
                    },
                  })}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Avatar
                      variant="rounded"
                      sx={(theme) => {
                        const tintMap = {
                          pdf: theme.palette.info.main,
                          image: theme.palette.success.main,
                          ppt: theme.palette.warning.main,
                          audio: theme.palette.secondary.main || "#A855F7",
                        };
                        const tint = tintMap[m.sourceType] || theme.palette.primary.main;
                        return {
                          width: 40,
                          height: 40,
                          backgroundColor: alpha(tint, 0.12),
                          color: tint,
                        };
                      }}
                    >
                      {m.sourceType === "pdf" ? (
                        <PictureAsPdfRoundedIcon fontSize="small" />
                      ) : m.sourceType === "image" ? (
                        <ImageRoundedIcon fontSize="small" />
                      ) : m.sourceType === "ppt" ? (
                        <SlideshowRoundedIcon fontSize="small" />
                      ) : (
                        <GraphicEqRoundedIcon fontSize="small" />
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} noWrap>
                        {m.title || m.fileName || "Untitled lecture"}
                      </Typography>
                      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.3 }}>
                        <StatusPill status={m.status} />
                        {m.creditsCost ? (
                          <Chip size="small" variant="outlined" label={`${m.creditsCost} credits`} sx={{ fontSize: 11 }} />
                        ) : null}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip arrow title={m.status === "ready" ? "Open study pack" : "Open"}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickMaterial(m);
                          }}
                        >
                          <OpenInNewRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip arrow title="Delete">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmDelete(m);
                          }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {uploadingKind ? (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                Uploading {uploadingKind.toUpperCase()}… {Math.round(uploadProgress * 100)}%
              </Typography>
              <LinearProgress
                variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
                value={uploadProgress * 100}
                sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
              />
            </Box>
          ) : null}
        </Paper>
      </MDBox>

      {/* ─── Modals ──────────────────────────────────────────────── */}
      <InsufficientCreditsModal
        open={!!insufficientCreditsState}
        required={insufficientCreditsState?.required}
        remaining={insufficientCreditsState?.remaining}
        onClose={() => setInsufficientCreditsState(null)}
      />

      <AskCopilotChatDialog
        open={askDialogOpen}
        onClose={() => setAskDialogOpen(false)}
        credits={credits}
        onCreditsChange={refreshCredits}
      />

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete this study pack?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            "{confirmDelete?.title || confirmDelete?.fileName || "Untitled lecture"}" and its chat history will be
            removed. Credits already spent cannot be refunded.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton variant="text" color="dark" onClick={() => setConfirmDelete(null)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={doDelete}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
